/**
 * This module defines all the exports for the `@qerent/lib` package.
 * 
 * BE VERY CAREFUL ABOUT REMOVING/RENAMING ANY EXPORTS HERE!  This will lead to breaking changes in tenant plugins.
 * 
 * When the package is built, the exports are bundled into a single module, which is published as `@qerent/lib`.
 * That module is then imported by third-party plugin developers, who can use all the components/actions/utilities exposed.
 * During module resolution (at runtime), the import map rewrites all relative imports for `@qerent/lib` back to this module.
 * This allows us to create an external package without having to segregate the web app into multiple packages.
 */

// Base styles...
import "./index.scss";

// Runtime patches the components rely on (moment-compatible Date methods incl. removeOffset used
// by DateOnly, ISO Date.toString, the JSON.stringify replacer, fetch retry/interceptors). The web
// app applies these via its own entry point; importing them here guarantees the same runtime for
// every other lib consumer (plugin sandboxes, tests)...
import "./shared/monkey-patches";

// Public types not directly exported...
export type {
    QerentComponent,
    ComponentDocumentation,
    AttributeDocumentation,
} from "./shared/components/DocumentedComponent";


// Namespace all base components...
import * as BaseComponentsModule from "./shared/components";
/**
 * The base UI components without any of the canvas data-binding features.
 * Used primarily when access is required to more low-level functionality or when custom data persistence logic is desired.
 */
export const BaseComponents = BaseComponentsModule;

// Hoist base components without a "viewer" counterpart to top-level entities...
export {
    SuccessAlert,
    ErrorAlert,
    WarningAlert,
    InfoAlert,
    DetailedErrorAlert,
    Avatar,
    Badge,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    ConfirmationDialog,
    InlineInfoTooltip,
    NoData,
    NoSearchResults,
    NothingYet,
    Popper,
    SearchTermHighlight,
} from "./shared/components/CanvasComponents";

// Export all our canvas components as top-level entities...
export * from "./viewer/components";

// Redux hooks...
export { useAppSelector } from "./store";
export { useAppActions } from "./shared/hooks";

// Other app hooks...
export { useAuth } from "./auth/AuthContext";
export { useScrollGate, SCROLL_GATE_ENABLED_ATTRIBUTE } from "./shared/hooks";
export { useSettings } from "./shared/providers/SettingsProvider";
export type { ClientAppSettings } from "./shared/providers/SettingsProvider";

// Plugin page support...

// App-shell components...
export { default as NavBar } from "./shared/components/NavBar";
export { default as Sidenav } from "./shared/components/Sidenav";
export type { NavBarProps } from "./shared/components/NavBar";
export type { MenuItem } from "./shared/components/Sidenav";
export type { BreadcrumbItem } from "./store/app/types";

// Individual NavBar components (allows users to roll fully custom page layouts)...
export { default as UserProfileBadge } from "./shared/components/UserProfileBadge";
export { AjaxLoadingIndicator } from "./shared/components/AjaxLoadingIndicator";
export { BranchSelector } from "./shared/components/BranchSelector";
export { ThemeSelector } from "./shared/components/ThemeSelector";

// Plugin module/route contracts...
export type { PluginRoute, PluginModule } from "./shared/services/pluginService";

// A pre-wired Dynamic Data API client...
export { useDatasourcesClient, createDatasourcesClient } from "./shared/hooks";
export type { PluginDataClient } from "./shared/hooks";
export { DateOnly } from "./shared/utilities";

// A schema-typed facade over the data client (plugins pass their generated OpenAPI `paths`)...
export { createPluginApi } from "./plugin/createPluginApi";
export type { PluginApi, PluginApiOptions, DatasourcePath, RequestOf, ResponseOf, DatasourceState } from "./plugin/createPluginApi";

// Early validation of a plugin's route/menu registrations (the host only surfaces these at runtime)...
export { validatePluginModule } from "./plugin/validatePluginModule";
export { RESERVED_TOP_LEVEL_SEGMENTS } from "./shared/services/pluginService";

// The JSON date revival applied to every datasource response, for test fixtures and manual parsing...
export { jsonParseReviver, reviveDates } from "./plugin/reviveDates";

// Theme CSS variable readers (chart configs can't consume var() expressions directly)...
export { getCssVariableValue } from "./shared/utilities";
export { useThemeColors } from "./shared/hooks/useThemeColors";

// Testing utilities...
export {
    QerentAppStub,
} from "../test";